// src/pages/TrustPage.tsx — Site officiel Vanessa : confiance et sécurité
import { Link } from 'react-router-dom';
import { Flag, EyeOff, Lock, Bot, LifeBuoy, UserX, ShieldAlert, Ban } from 'lucide-react';
import { Section, Grid, FeatureCard, CtaBand, PageHeader } from '@/components/site/blocks';
import { CONTACT_EMAIL } from '@/config/site';

const RULES = [
    { icon: UserX, text: 'Ne ciblez jamais une personne réelle par son nom complet ou des détails qui l’identifient clairement dans une accusation grave (infidélité, délit…) sans preuve.' },
    { icon: ShieldAlert, text: 'Aucun contenu sexuel impliquant des personnes réelles, et jamais concernant des mineurs : tolérance zéro.' },
    { icon: Ban, text: 'Pas de harcèlement, de menaces, ni de divulgation de données privées (adresse, numéro…) sans l’accord de la personne.' },
    { icon: EyeOff, text: 'L’anonymat protège votre identité vis-à-vis des autres utilisateurs, pas vis-à-vis de la plateforme en cas de signalement grave.' },
    { icon: Flag, text: 'Signalez tout contenu qui vous semble abusif : l’équipe de modération traite chaque signalement.' },
];

export default function TrustPage() {
    return (
        <>
            <PageHeader
                eyebrow="Confiance et sécurité"
                title="Parler librement, sans abandonner la responsabilité."
                intro="Une plateforme de ragots et de témoignages ne tient que si elle est sûre. Voici ce que nous faisons, et ce que nous ne promettons pas."
            />

            <Section title="Nos engagements">
                <Grid cols={2}>
                    <FeatureCard icon={Flag} title="Modération">
                        Chaque contenu peut être signalé. L’équipe l’examine et peut le masquer, le supprimer ou sanctionner son auteur. Les bons plans de Ça sert passent par une vérification avant d’être publics.
                    </FeatureCard>
                    <FeatureCard icon={EyeOff} title="Anonymat, et ses limites">
                        Publier en anonyme masque votre nom aux autres personnes. La plateforme conserve toutefois une trace technique de l’auteur, uniquement pour la modération en cas de signalement grave.
                    </FeatureCard>
                    <FeatureCard icon={Lock} title="Vos conversations">
                        Vos échanges avec Vanessa servent à faire fonctionner le service. Ce qu’elle retient de vous se consulte et s’efface depuis votre profil. Le détail est dans la{' '}
                        <Link to="/confidentialite" className="font-semibold text-ochre underline">politique de confidentialité</Link>.
                    </FeatureCard>
                    <FeatureCard icon={Bot} title="Une IA transparente">
                        Vanessa est une intelligence artificielle et ne prétend jamais être une personne réelle. Elle peut se tromper : elle ne remplace ni un avis professionnel ni la justice.
                    </FeatureCard>
                </Grid>
            </Section>

            <Section id="regles" tone="sand" title="Les règles de la communauté" intro="Ces cinq règles sont présentées à chaque nouvelle personne à son arrivée.">
                <ul className="grid gap-4 md:grid-cols-2">
                    {RULES.map(({ icon: Icon, text }) => (
                        <li key={text} className="flex items-start gap-3 rounded-2xl bg-white p-5">
                            <Icon className="mt-0.5 h-5 w-5 shrink-0 text-ochre" aria-hidden="true" />
                            <p className="text-base leading-relaxed text-gray-700">{text}</p>
                        </li>
                    ))}
                </ul>
            </Section>

            <Section>
                <div className="flex flex-col gap-4 rounded-3xl bg-brand-tint p-8 sm:flex-row sm:items-start">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white text-ochre">
                        <LifeBuoy className="h-6 w-6" aria-hidden="true" />
                    </span>
                    <div>
                        <h2 className="font-display text-2xl font-bold text-ink">Si vous traversez un moment difficile</h2>
                        <p className="mt-2 max-w-2xl text-lg text-gray-700">
                            Face à une détresse, Vanessa quitte le registre de l’humour et reste à l’écoute. Elle n’est pas un service d’urgence : en cas de danger immédiat, contactez les secours de votre pays ou une personne de confiance.
                        </p>
                    </div>
                </div>
            </Section>

            <CtaBand
                title="Un contenu vous inquiète ?"
                text="Utilisez le bouton « Signaler » dans l’application, ou écrivez-nous."
                primary={{ label: 'Nous écrire', href: `mailto:${CONTACT_EMAIL}` }}
                secondary={{ label: 'Conditions d’utilisation', to: '/conditions', variant: 'secondary' }}
            />
        </>
    );
}
